import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { Form, Field } from "react-final-form";
import Image from "next/image";

import { SelectInput, NumberInput, TextInput } from "../../ui";
import { BannerImage } from "../";
import styles from "./HomePageComponent.module.css";
import { useRandomPattern } from "../../../lib/avatarTrainingAIAPI/swr/avatartraining";

export interface HomePageSheetValues {
  randomPattern: number;
  language: string;
  mode: string;
  role: string;
  avatarType: string;
  experimentId: number;
  clientSid: string;
}

export interface HomePageComponentProps {
  onSubmit: (values: HomePageSheetValues) => void;
}

const getAvatarTypeOptions = (language: string) => {
  if (language === "ja") {
    return ["sue_adv", "csa", "sue"];
  }
  if (language === "en") {
    return ["csa", "witness", "en_kind"];
  }
  if (["lt", "fi", "zh"].includes(language)) {
    return ["csa"];
  }
  if (language === "et") {
    return ["witness"];
  }
  return ["csa"];
};

/** Select a valid locale default without coupling it to option display order. */
const getInitialAvatarType = (
  language: string,
  avatarTypeOptions: string[],
) => {
  const preferredAvatarType =
    language === "en" ? "en_kind" : avatarTypeOptions[0];
  return avatarTypeOptions.includes(preferredAvatarType)
    ? preferredAvatarType
    : avatarTypeOptions[0] ?? "csa";
};

const getModeConfig = (language: string, avatarType: string) => {
  if (language === "ja" && avatarType === "csa") {
    return { selectableModes: ["llm", "ai"], defaultMode: "ai" };
  }
  if (language === "ja" && avatarType === "sue") {
    return { selectableModes: [], defaultMode: "op" };
  }
  if (language === "ja" && avatarType === "sue_adv") {
    return { selectableModes: ["llm"], defaultMode: "llm" };
  }
  if (language === "lt" && avatarType === "csa") {
    return { selectableModes: [], defaultMode: "op" };
  }
  if (language === "en" && avatarType === "csa") {
    return { selectableModes: [], defaultMode: "ai" };
  }
  if (language === "en" && avatarType === "witness") {
    return { selectableModes: [], defaultMode: "op" };
  }
  if (language === "en" && avatarType === "en_kind") {
    return { selectableModes: [], defaultMode: "op" };
  }
  if (language === "et" && avatarType === "witness") {
    return { selectableModes: [], defaultMode: "op" };
  }
  if (language === "fi" && avatarType === "csa") {
    return { selectableModes: [], defaultMode: "ai" };
  }
  if (language === "zh" && avatarType === "csa") {
    return { selectableModes: [], defaultMode: "ai" };
  }
  return { selectableModes: [], defaultMode: "ai" };
};

const getRandomPatternLabel = (
  label: string,
  randomPatternNumber: number,
  t: (key: string) => string,
) => {
  if (label && label.trim().length > 0) {
    return label;
  }
  return t(`startPage:randomPattern${randomPatternNumber}`);
};

const HomePageFormFields = ({
  language,
  values,
  form,
  t,
}: {
  language: string;
  values: HomePageSheetValues;
  form: any;
  t: (key: string) => string;
}) => {
  const modeConfig = getModeConfig(language, values.avatarType);
  const avatarTypeOptions = getAvatarTypeOptions(language);
  const { data, isLoading } = useRandomPattern({
    language,
    avatarType: values.avatarType,
    mode: values.mode,
  });
  const randomPatterns = data?.randomPatterns ?? [];
  const showModeSelect =
    values.role === "interviewer" && modeConfig.selectableModes.length > 0;
  const showAvatarTypeSelect =
    values.role === "interviewer" && ["ja", "en"].includes(language);

  useEffect(() => {
    if (modeConfig.selectableModes.length > 0) {
      if (!modeConfig.selectableModes.includes(values.mode)) {
        form.change("mode", modeConfig.selectableModes[0]);
      }
      return;
    }
    if (values.mode !== modeConfig.defaultMode) {
      form.change("mode", modeConfig.defaultMode);
    }
  }, [modeConfig, values.mode, form]);

  useEffect(() => {
    if (randomPatterns.length === 0) {
      return;
    }
    const patternNumbers = randomPatterns.map(
      (pattern: any) => pattern.randomPatternNumber,
    );
    if (!patternNumbers.includes(Number(values.randomPattern))) {
      form.change("randomPattern", randomPatterns[0].randomPatternNumber);
    }
  }, [randomPatterns, values.randomPattern, form]);

  return (
    <>
      {/* Available language for each combination of avatar type and mode */}
      {/* CSA AI: En, Ja, Fi, Zh */}
      {/* CSA Op: Lt */}
      {/* Witness AI: */}
      {/* Witness Op: Et, En */}
      <div
        className={styles.fieldWrapper}
        hidden={
          (values.avatarType === "csa" && !["lt"].includes(language)) ||
          (values.avatarType === "sue_adv" && ![""].includes(language)) ||
          (values.avatarType === "witness" && !["et", "en"].includes(language))
        }
      >
        <label className={styles.labelText} htmlFor="role">
          {t("startPage:role")}
        </label>
        <Field<string> id="role" name="role" component={SelectInput}>
          <option value="interviewer">{t("startPage:interviewer")}</option>
          {/* Currently operator version is for Witness and CSA of Lt */}
          <option value="operator">{t("startPage:operator")}</option>
        </Field>
      </div>
      <div className={styles.fieldWrapper} hidden={!showModeSelect}>
        <label className={styles.labelText} htmlFor="mode">
          {t("startPage:mode")}
        </label>
        <Field<string> id="mode" name="mode" component={SelectInput}>
          {modeConfig.selectableModes.includes("llm") && (
            <option value="llm">{t("startPage:llmMode")}</option>
          )}
          {modeConfig.selectableModes.includes("ai") && (
            <option value="ai">{t("startPage:aiMode")}</option>
          )}
        </Field>
      </div>
      <div className={styles.fieldWrapper} hidden={!showAvatarTypeSelect}>
        <label className={styles.labelText} htmlFor="avatarType">
          {t("startPage:avatarType")}
        </label>
        <Field<string>
          id="avatarType"
          name="avatarType"
          component={SelectInput}
        >
          {avatarTypeOptions.includes("sue_adv") && (
            <option value="sue_adv">{t("startPage:sueAdv")}</option>
          )}
          {avatarTypeOptions.includes("sue") && (
            <option value="sue">{t("startPage:sue")}</option>
          )}
          {avatarTypeOptions.includes("csa") && (
            <option value="csa">{t("startPage:csa")}</option>
          )}
          {avatarTypeOptions.includes("witness") && (
            <option value="witness">{t("startPage:witness")}</option>
          )}
          {avatarTypeOptions.includes("en_kind") && (
            <option value="en_kind">{t("startPage:kind")}</option>
          )}
        </Field>
      </div>
      <div
        className={styles.fieldWrapper}
        hidden={values.role !== "interviewer"}
      >
        <label className={styles.labelText} htmlFor="randomPattern">
          {t("startPage:randomPattern")}
        </label>
        <Field<string>
          id="randomPattern"
          name="randomPattern"
          component={SelectInput}
        >
          {isLoading && (
            <option value="" disabled>
              {t("startPage:loading")}
            </option>
          )}
          {!isLoading &&
            randomPatterns.map((pattern: any) => (
              <option
                key={pattern.randomPatternNumber}
                value={pattern.randomPatternNumber}
              >
                {getRandomPatternLabel(
                  pattern.label,
                  pattern.randomPatternNumber,
                  t,
                )}
              </option>
            ))}
        </Field>
      </div>
    </>
  );
};

const HomePageComponent = ({ onSubmit }: HomePageComponentProps) => {
  const { t } = useTranslation();
  const language = i18n.language;
  const avatarTypeOptions = getAvatarTypeOptions(language);
  const initialAvatarType = getInitialAvatarType(language, avatarTypeOptions);
  const initialMode = getModeConfig(language, initialAvatarType).defaultMode;
  const initialValues: HomePageSheetValues = {
    randomPattern: 0,
    language,
    mode: initialMode,
    role: "interviewer",
    avatarType: initialAvatarType,
    experimentId: 0,
    clientSid: "",
  };

  return (
    <div style={{ textAlign: "center" }}>
      <Form
        onSubmit={onSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, form, values }) => (
          <form onSubmit={handleSubmit}>
            <BannerImage language={language} avatarType={values.avatarType} />
            <span
              style={{
                width: "100%",
                paddingBottom: "10px",
                display: "inline-block",
              }}
            />
            <h2>
              {values.avatarType === "witness"
                ? t("startPage:titleHomeWitness")
                : t("startPage:titleHome")}
            </h2>
            <span
              style={{
                width: "100%",
                paddingBottom: "10px",
                display: "inline-block",
              }}
            />
            {language === "fi" && (
              <>
                <Image
                  src={"/images/eu-logo.png"}
                  alt="eu"
                  width={600}
                  height={100}
                />
                <span
                  style={{
                    width: "100%",
                    paddingBottom: "10px",
                    display: "inline-block",
                  }}
                />
              </>
            )}
            {language === "fi" && (
              <>
                <p className={styles.disclaimer}>
                  {t("startPage:euDisclaimer")}
                </p>
                <span
                  style={{
                    width: "100%",
                    paddingBottom: "10px",
                    display: "inline-block",
                  }}
                />
              </>
            )}
            <HomePageFormFields
              language={language}
              values={values}
              form={form}
              t={t}
            />
            <div
              className={styles.fieldWrapper}
              hidden={values.role !== "operator"}
            >
              <label htmlFor="experimentId">
                {t("startPage:experimentId")}
              </label>
              <Field
                id="experimentId"
                name="experimentId"
                component={NumberInput}
              />
            </div>
            <div
              className={styles.fieldWrapper}
              hidden={values.role !== "operator"}
            >
              <label htmlFor="clientSid">{t("startPage:clientSid")}</label>
              <Field id="clientSid" name="clientSid" component={TextInput} />
            </div>
            <div className={styles.fieldWrapper}>
              <Field<string>
                id="language"
                name="language"
                component={SelectInput}
                value={language}
                hidden
              >
                <option value="ja">{t("startPage:ja")}</option>
                <option value="en">{t("startPage:en")}</option>
                <option value="zh">{t("startPage:zh")}</option>
                <option value="lt">{t("startPage:lt")}</option>
                <option value="et">{t("startPage:et")}</option>
              </Field>
            </div>
            <span
              style={{
                width: "100%",
                paddingBottom: "10px",
                display: "inline-block",
              }}
            />
            <div className="buttons">
              <button
                value={"next"}
                className={styles.controlButton}
                type="submit"
                disabled={false}
              >
                {t("common:start")}
              </button>
            </div>
          </form>
        )}
      />
    </div>
  );
};

export { HomePageComponent };
